/* Guided Add to Cart — HEllO EDC, 22 Sep 2026. Domestic (India) store.
   v7 — v6 redesigned the button after a customer-journey review found it had
   become too easy to miss; v7 fixes the sold-out flicker a screen recording
   sent the same day, once compared against the button's post-redesign code.

   NEW BEHAVIOUR (replaces the CHOOSE YOUR ... prompt-cycling of v1-v5):
   - The button always reads ADD TO CART. It is brand red at all times —
     semi-transparent while a required choice is still open, full opacity
     once everything is settled — so it is never mistaken for an inert or
     disabled control the way the old dark/faded look could be.
   - Pressing it while something is still open does not scroll the page to
     "somewhere near the variant picker" — it opens the drawer for the next
     unmade choice directly (Lug Width, then Buckle/Clasp, then Length, then
     Keepers — whichever apply), the same way tapping that option's own
     grid button would. Any step already settled is skipped regardless of
     the order the shopper actually worked through them in. Each press
     therefore both reveals and lets the shopper resolve one more choice,
     so reaching ADD TO CART costs exactly as many taps as there are
     choices to make — never a wasted one.
   - Colour is no longer part of this sequence at all. It now has its own
     always-visible swatches directly under the image carousel
     (snippets/product-variant-picker.liquid + assets/wc-color-swatches.*),
     so it is browsed rather than drawer-gated — one whole step removed
     from the count for every shopper. The sequence now starts at Lug
     Width.

   INHERITED FROM EARLIER VERSIONS, UNCHANGED:
   - wc-atc-state.js's genuine sold-out/unavailable styling is deferred to
     until every step THIS file tracks is settled, so an in-progress
     selection is never mistaken for an unbuyable variant (v3).
   - The click interception runs on `window` in the capture phase, ahead of
     any `document`-level handler (v3).
   - Touch/selection tracking uses both `click` and `change`, and excludes
     the drawer's own trigger/close/handle/summary elements from counting
     as a selection made inside it (v2).
   - Real per-device press feedback (pointerdown/up, not CSS :active alone,
     which WebKit/Android can silently refuse to apply to a delegated
     listener) and an "ADDING..." busy state mirroring the hidden native
     submit button's ~2s loading window, armed optimistically the instant a
     real add-to-cart tap is forwarded rather than only once the native
     button's own class/attribute confirms it (v5).

   WHERE THE ANSWERS, AND THE WAY TO OPEN EACH DRAWER, COME FROM

   - Native variant options (Lug Width, Size, and, on some products, a
     native Buckle/Clasp or Length option) live under <variant-selects> as
     fieldset/dropdown elements carrying data-option-index. An option with
     only one possible value needs no choice and counts as settled on its
     own; anything with more than one is settled only once the shopper has
     actually clicked something inside it — a pre-selected first value
     does not count. Opening one of these on tap reuses the theme's own
     mechanism: clicking `.mobile-variant-btn[data-option-index]` is
     exactly what the shopper's own tap on that grid button does
     (snippets/product-variant-picker.liquid's own script owns opening the
     drawer from there); a desktop fallback opens the accordion directly
     and scrolls to it.

   - The buckle/clasp DRAWER (assets/wc-buckle.js) is read from
     window.WCBuckle.confirmed for whether it is settled; its total absence
     means the product has no buckle drawer at all, so that step is
     skipped. Wherever it actually renders, it lives inside the theme's own
     "Clasp Type"/"Buckle/Clasp Type" accordion (found by id, or by its
     heading text, exactly as wc-buckle.js itself finds it) — so it opens
     through the very same data-option-index mechanism as any native
     option once that fieldset has been located.

   - Keepers (Easify Options, wrapped by assets/wc-keepers-drawer.js) have
     no public state object, only a DOM tell: the trigger button's caption
     reads the literal word "Select" until something has been picked, and
     no data-option-index of its own — opening it means clicking its own
     trigger, the same tap the shopper would make.

   THE SOLD-OUT FLICKER FIX (v7)

   The recording showed the button flipping between "ADDING..." and "SOLD
   OUT" several times a second on a genuinely out-of-stock variant, not
   settling on either. Two changes, both in this file, once guided
   sequencing itself is out of the way (an unresolved required option still
   wins over unbuyable, exactly as in v6 — an unfinished form isn't proof
   the product can't be bought):

   - paint() now checks unbuyable BEFORE the busy/"ADDING..." state, and
     always wins — clearing busy immediately rather than leaving it to the
     6s safety timeout or a real-button mutation a disabled button never
     produces. Previously busy was checked first, so a stray busy flag could
     outrank the truth for as long as it stayed set.
   - watchClicks() now swallows a tap on an already-unbuyable button before
     it can set busy at all. A shopper tapping a SOLD OUT button more than
     once — an understandable reaction to a button that looks unresponsive —
     used to fire a fresh "ADDING..." flash on every tap, each one racing the
     correct SOLD OUT repaint that followed it.

   THE FLICKER'S ACTUAL ROOT CAUSE, FOUND AND FIXED (24 Sep 2026)

   v7 still flickered because "unbuyable" was read from wc-atc-state.js's own
   DOM class (`.wc-unbuyable`) rather than computed here. That class is
   written by wc-atc-state.js on ITS OWN debounced MutationObserver (60ms),
   while this file repaints on a different debounced MutationObserver
   (100ms), an 80ms `change` timeout, an un-debounced `wc-buckle:change`
   listener, and a 700ms safety interval. Every one of those firing after a
   colour swatch or drawer change had NO guaranteed order relative to
   wc-atc-state.js's own repaint — so this file could read the class before
   or after it reflected the new variant, and would repaint the button with
   whichever answer was stale at that instant. Two independent clocks racing
   over shared DOM state is exactly what produced the flip-flopping in the
   recording.

   The fix removes the dependency on that class entirely: unbuyable() below
   reads the exact same source wc-atc-state.js reads — the selected-variant
   JSON the theme already inlines — and computes the same true/false answer
   directly, in this file's own paint(), on this file's own tick. There is
   nothing left to race; both scripts still repaint independently, but each
   one is now always correct the instant it runs, not just eventually. */

(function () {
  'use strict';

  if (window.__wcGuidedAtc) return;
  window.__wcGuidedAtc = true;

  var ORANGE = '#ef4532';
  /* 9 Oct 2026: the pending state is no longer dimmed. A faded button read as
     "out of stock"; it now shows full brand colour from the start, and a tap
     while something is still unchosen opens that drawer as before. */
  var PENDING_OPACITY = '1';

  var touched = {};
  var guidedPending = false;
  var lastNext = null;
  var busy = false;

  function style() {
    if (document.getElementById('wc-guided-atc-css')) return;
    var s = document.createElement('style');
    s.id = 'wc-guided-atc-css';
    s.textContent =
      '.dummy-add-to-cart{-webkit-tap-highlight-color:transparent;outline:none;' +
        'transition:transform .08s ease,filter .08s ease,opacity .18s ease;}' +
      '.dummy-add-to-cart:active,' +
      '.dummy-add-to-cart.wc-guided-active{transform:scale(.97);filter:brightness(.9);}' +
      '.dummy-add-to-cart.wc-guided-pending{cursor:pointer;}' +
      '.dummy-add-to-cart.wc-guided-complete{cursor:pointer;}' +
      '@keyframes wcGuidedAtcPulse{0%,100%{opacity:1}50%{opacity:.7}}' +
      '.dummy-add-to-cart.wc-guided-busy{animation:wcGuidedAtcPulse .9s ease-in-out infinite;cursor:wait;}';
    document.head.appendChild(s);
  }

  /* Explicit press feedback, independent of CSS :active. Bound on window in
     the capture phase so it fires for a touch OR a mouse press, before any
     other script on the page. pointerdown/pointerup covers modern
     touch+mouse in one pair on every current browser; touchstart/touchend
     and mousedown/mouseup are kept alongside as a safety net for the rare
     browser that does not fire pointer events reliably (some in-app
     webviews). Whichever fires first adds the class; whichever fires to
     end the press removes it — duplicates are harmless since it is just a
     classList toggle. */
  function watchPress() {
    function on(e) {
      var atc = e.target.closest && e.target.closest('.dummy-add-to-cart');
      if (atc) atc.classList.add('wc-guided-active');
    }
    function off() {
      document.querySelectorAll('.dummy-add-to-cart.wc-guided-active').forEach(function (el) {
        el.classList.remove('wc-guided-active');
      });
    }
    ['pointerdown', 'touchstart', 'mousedown'].forEach(function (type) {
      window.addEventListener(type, on, true);
    });
    ['pointerup', 'pointercancel', 'touchend', 'touchcancel', 'mouseup', 'mouseleave'].forEach(function (type) {
      window.addEventListener(type, off, true);
    });
  }

  /* ---------- the same "is this variant genuinely unbuyable" truth
     wc-atc-state.js computes, read from the same source, independently.

     Deliberately duplicated rather than imported: reading wc-atc-state.js's
     OWN class (`.wc-unbuyable`) is what produced the flicker (see the file
     header) — that class is only ever as fresh as wc-atc-state.js's last,
     separately-timed repaint. Computing it here too means this file's
     paint() is correct on its own schedule, not dependent on another
     script's. Same three states wc-atc-state.js recognises: an existing
     variant with stock is buyable; an existing variant with none is
     "soldout"; a combination Shopify never created a variant for at all is
     "unavailable" (the theme inlines null for the selected-variant JSON in
     that case). Either of the latter two makes the button unbuyable. */
  function unbuyable() {
    var el = document.querySelector('variant-selects script[data-selected-variant]');
    if (!el) return false;           // no picker on this page — nothing to judge
    var v;
    try { v = JSON.parse(el.textContent); } catch (e) { return false; }
    if (v === null) return true;     // combination was never made
    return v && v.available === false;
  }

  /* ---------- native variant options ---------- */

  function nativeFieldsets() {
    var scope = document.querySelector('variant-selects');
    if (!scope) return [];
    var nodes = scope.querySelectorAll('[data-option-index]');
    var seen = {};
    var out = [];
    nodes.forEach(function (n) {
      if (n.tagName !== 'FIELDSET' && !n.classList.contains('product-form__input--dropdown')) return;
      var idx = n.getAttribute('data-option-index');
      if (idx == null || seen[idx]) return;
      seen[idx] = true;
      out.push({ index: idx, el: n });
    });
    return out;
  }

  /* The mobile 2-column grid is rendered for every option regardless of
     viewport (the theme hides it with CSS on wider screens, it is not
     conditional in the markup), so its label span is the one reliable,
     already-worded-for-shoppers copy of each option's name. */
  function labelFor(fs) {
    var btn = document.querySelector('.mobile-variant-btn[data-option-index="' + fs.index + '"] .mobile-variant-btn__label');
    var text = btn ? btn.textContent : '';
    if (!text || !text.trim()) {
      var legend = fs.el.querySelector('legend, .form__label');
      text = legend ? legend.textContent : '';
      var selected = fs.el.querySelector('[data-selected-value]');
      if (selected && text) text = text.replace(selected.textContent, '');
    }
    if (!text || !text.trim()) {
      var h2 = fs.el.querySelector('.accordion__title');
      text = h2 ? h2.textContent : '';
    }
    return (text || 'OPTION').replace(/:\s*$/, '').trim().toUpperCase();
  }

  function valueCount(fs) {
    var inputs = fs.el.querySelectorAll('input[type="radio"]');
    if (inputs.length) return inputs.length;
    return fs.el.querySelectorAll('select option').length;
  }

  function rankFor(label) {
    if (label.indexOf('SIZE') > -1 || label.indexOf('LUG') > -1) return 0;
    if (label.indexOf('CLASP') > -1 || label.indexOf('BUCKLE') > -1) return 1;
    if (label.indexOf('LENGTH') > -1) return 2;
    return 4;
  }

  function watchTouch() {
    /* A `change` event never fires for re-selecting an already-checked
       radio, so click is tracked too — but `.mobile-variant-btn` (the
       trigger that OPENS a drawer/accordion) itself carries
       data-option-index and is a <button>, so a plain closest() match was
       counting the tap that merely opened the drawer as a pick made inside
       it. Same problem for the drawer's close control, its drag handle,
       and an accordion's <summary> header. All four are excluded — only a
       click on the actual swatch/radio/label/option inside an open drawer
       counts as a choice. */
    document.addEventListener('click', function (e) {
      if (e.target.closest('.mobile-variant-btn, .mobile-drawer-close, .drawer-handle, summary')) return;

      var control = e.target.closest('input, label, img, select, option, button');
      if (!control) return;
      var container = control.closest('[data-option-index]');
      if (container) touched[container.getAttribute('data-option-index')] = true;
    }, true);

    document.addEventListener('change', function (e) {
      var container = e.target.closest && e.target.closest('[data-option-index]');
      if (container) touched[container.getAttribute('data-option-index')] = true;
    }, true);
  }

  /* ---------- buckle/clasp drawer (metafield-driven, may be absent) ---------- */

  function buckleApplicable() {
    return !!window.WCBuckle;
  }

  function buckleSettled() {
    return !!(window.WCBuckle && window.WCBuckle.confirmed);
  }

  /* Finds the fieldset the buckle/clasp selector actually lives inside, the
     same way assets/wc-buckle.js's own claspAccordion() does: by the
     theme's own id first, then by heading text for the rare product whose
     option name doesn't match exactly. Knowing this fieldset's
     data-option-index is what lets a tap on the ADD TO CART button open
     this drawer the same way it opens any native option's. */
  function buckleFieldset() {
    var acc = document.querySelector('#variant-accordion-clasp-type');
    if (!acc) {
      var all = document.querySelectorAll('variant-selects details.variant-accordion');
      for (var i = 0; i < all.length; i++) {
        var h = all[i].querySelector('.accordion__title');
        if (h && /clasp|buckle/i.test(h.textContent)) { acc = all[i]; break; }
      }
    }
    return acc ? acc.closest('fieldset[data-option-index]') : null;
  }

  /* ---------- keepers (Easify Options) ---------- */

  function keeperTriggers() {
    return document.querySelectorAll('.mobile-variant-btn--rebel-keepers');
  }

  function keepersSettled() {
    var triggers = keeperTriggers();
    for (var i = 0; i < triggers.length; i++) {
      var v = triggers[i].querySelector('.mobile-variant-btn__value-option');
      var text = v ? v.textContent.trim() : '';
      if (!text || text === 'Select') return false;
    }
    return true;
  }

  /* ---------- put it together ---------- */

  function buildSteps() {
    var steps = [];

    nativeFieldsets().forEach(function (fs) {
      var label = labelFor(fs);
      /* Colour now has its own always-visible swatches under the carousel
         (assets/wc-color-swatches.js) and is deliberately left out of this
         sequence entirely — it is no longer drawer-gated, so it should
         neither block the button's full-opacity state nor be something a
         tap on the button opens. */
      if (label === 'COLOUR WAY' || label === 'COLOR' || label === 'COLOUR') return;
      var count = valueCount(fs);
      var settled = count <= 1 || !!touched[fs.index];
      steps.push({ rank: rankFor(label), settled: settled, idx: fs.index, kind: 'native', el: fs.el });
    });

    if (buckleApplicable()) {
      var bfs = buckleFieldset();
      steps.push({
        rank: 1,
        settled: buckleSettled(),
        idx: bfs ? bfs.getAttribute('data-option-index') : null,
        kind: 'buckle',
        el: bfs || document.querySelector('[data-wc-buckle-data]')
      });
    }

    if (keeperTriggers().length) {
      steps.push({ rank: 3, settled: keepersSettled(), kind: 'keepers', el: keeperTriggers()[0] });
    }

    steps.sort(function (a, b) { return a.rank - b.rank; });
    return steps;
  }

  /* Exposed for wc-atc-state.js. 24 Sep 2026: that file used to paint its own
     genuine sold-out/unavailable state onto the button any time the native
     selected variant was bad — which, for as long as a required option here
     is still open, is most of the time a shopper spends on the page, since
     Shopify has nothing to match until every option is chosen. That file's
     own timers and this file's own timers are independent, so the two kept
     overwriting each other's paint of the same button for as long as
     anything was unresolved — the flicker persisted the whole time, only
     stopping once every option was picked. Recomputed fresh from buildSteps()
     on every call rather than read off a stored flag, so whoever calls this
     always gets a live answer, never a stale one — the same principle as
     unbuyable() below reading wc-atc-state.js's own source directly instead
     of its class. */
  window.__wcGuidedIncomplete = function () {
    if (!document.querySelector('.dummy-add-to-cart')) return false;
    if (!document.querySelector('variant-selects')) return false;
    var steps = buildSteps();
    for (var i = 0; i < steps.length; i++) {
      if (!steps[i].settled) return true;
    }
    return false;
  };

  /* A screen recording sent 22 Sep 2026 showed the button rapidly alternating
     between "ADDING..." and "SOLD OUT" on a genuinely out-of-stock variant —
     several flips a second, not a one-off. Root cause (v7): once every step
     this file tracks was settled, paint() checked `busy` BEFORE it checked
     unbuyable — so a busy flag left over from a tap (or set by a later tap
     on what turned out to be a disabled button, see watchClicks() below)
     could outrank the true sold-out state for as long as it stayed set,
     instead of the reverse. unbuyable now wins outright whenever it is
     checked, and clears busy immediately rather than leaving that to the 6s
     safety timeout or a real-button mutation a disabled button never
     produces.

     A second recording sent 24 Sep 2026, after v7, showed the same flicker
     surviving a colour change even with every step already settled. That
     was the cross-script race described in the file header — fixed by
     calling unbuyable() (computed independently, above) instead of reading
     wc-atc-state.js's `.wc-unbuyable` class. The check still only runs once
     guided sequencing is out of the way — an unresolved required option
     still wins over unbuyable, exactly as before, since an unfinished form
     isn't proof the product can't be bought. */
  function paint() {
    var atc = document.querySelector('.dummy-add-to-cart');
    if (!atc) return;

    var steps = buildSteps();
    var next = null;
    for (var i = 0; i < steps.length; i++) {
      if (!steps[i].settled) { next = steps[i]; break; }
    }

    var text = 'ADD TO CART';

    if (next) {
      /* Something this file tracks is still unchosen. This always wins
         over wc-atc-state.js's sold-out/unavailable styling — an
         unresolved variant at this point just means a required option
         hasn't been picked yet, not that the product can't be bought.
         The button itself never changes its wording any more — brand red
         at all times, just dimmed until everything is settled — so it
         always reads as ADD TO CART, obvious and reachable, rather than
         looking inactive. */
      guidedPending = true;
      lastNext = next;

      if ((atc.textContent || '').trim() !== text) atc.textContent = text;
      atc.setAttribute('data-wc-label', text);
      atc.classList.remove('wc-guided-complete', 'wc-guided-busy');
      atc.classList.add('wc-guided-pending');

      atc.style.setProperty('background-color', ORANGE, 'important');
      atc.style.setProperty('border-color', ORANGE, 'important');
      atc.style.setProperty('color', '#fff', 'important');
      atc.style.setProperty('opacity', PENDING_OPACITY, 'important');
      return;
    }

    guidedPending = false;
    lastNext = null;

    /* Everything this file tracks has been chosen. Computed independently
       (see unbuyable(), above) rather than read off wc-atc-state.js's own
       class, so this is never a stale answer from a different script's
       repaint clock. If the variant is genuinely sold out or was never
       made, step aside completely — including clearing any busy flag left
       over from an earlier tap. */
    if (unbuyable()) {
      if (busy) {
        busy = false;
        clearTimeout(busyTimeout);
      }
      atc.classList.remove('wc-guided-complete', 'wc-guided-pending', 'wc-guided-busy');
      atc.style.removeProperty('background-color');
      atc.style.removeProperty('border-color');
      atc.style.removeProperty('color');
      atc.style.removeProperty('opacity');
      return;
    }

    /* The real add-to-cart fetch (~2s) happens on the hidden native submit
       button; this dummy button never reflected that wait, which looked
       identical to a dead click. While busy, this wins over the completed
       state — text, colour, all of it — until the real button reports it
       is done. It can no longer win over a genuinely unbuyable variant: that
       check, above, always runs first. */
    if (busy) {
      var busyText = 'ADDING...';
      if ((atc.textContent || '').trim() !== busyText) atc.textContent = busyText;
      atc.setAttribute('data-wc-label', busyText);
      atc.classList.remove('wc-guided-pending');
      atc.classList.add('wc-guided-complete', 'wc-guided-busy');

      atc.style.setProperty('background-color', ORANGE, 'important');
      atc.style.setProperty('border-color', ORANGE, 'important');
      atc.style.setProperty('color', '#fff', 'important');
      atc.style.removeProperty('opacity');
      return;
    }

    if ((atc.textContent || '').trim() !== text) atc.textContent = text;
    atc.setAttribute('data-wc-label', text);
    atc.classList.remove('wc-guided-pending', 'wc-guided-busy');
    atc.classList.add('wc-guided-complete');

    atc.style.setProperty('background-color', ORANGE, 'important');
    atc.style.setProperty('border-color', ORANGE, 'important');
    atc.style.setProperty('color', '#fff', 'important');
    atc.style.removeProperty('opacity');
  }

  function scrollToNext() {
    var target = (lastNext && lastNext.el) || document.querySelector('variant-selects');
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /* Opens the drawer for the given step, the same way the shopper's own tap
     on that option's grid button would — rather than merely scrolling to
     it. Native options and the buckle/clasp drawer both resolve to a
     data-option-index and share one mechanism; keepers has no drawer of
     its own to target, only its trigger. */
  function openStep(step) {
    if (!step) return;

    if (step.kind === 'keepers') {
      var trigger = keeperTriggers()[0];
      if (trigger && trigger.click) { trigger.click(); return; }
      scrollToNext();
      return;
    }

    var idx = step.idx;
    if (idx != null) {
      var btn = document.querySelector('.mobile-variant-btn[data-option-index="' + idx + '"]');
      if (btn && window.innerWidth < 750) { btn.click(); return; }

      var fs = document.querySelector(
        'variant-selects fieldset[data-option-index="' + idx + '"], ' +
        'variant-selects .product-form__input--dropdown[data-option-index="' + idx + '"]'
      );
      if (fs) {
        var det = fs.querySelector('details.variant-accordion');
        if (det && !det.open) {
          var sum = det.querySelector('summary');
          if (sum) sum.click(); else det.open = true;
        }
        if (fs.scrollIntoView) {
          try { fs.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
          catch (e) { fs.scrollIntoView(); }
        }
        return;
      }
    }

    scrollToNext();
  }

  function watchClicks() {
    /* On window, capture phase. Capture-phase listeners fire outward-in —
       window, then document, then down toward the target — in that order
       regardless of which script registered first, so a window-level
       listener is guaranteed to see this click before wc-atc-state.js's
       own document-level click handler, or the theme's own handler that
       forwards this click to the real submit button
       (sections/main-product.liquid), get a chance to act on it. */
    window.addEventListener('click', function (e) {
      var atc = e.target.closest && e.target.closest('.dummy-add-to-cart');
      if (!atc) return;

      if (!guidedPending) {
        /* Everything THIS file tracks is settled. If the resulting variant
           is genuinely sold out or doesn't exist, there is nothing to add —
           swallow the tap here, before anything sets busy, rather than
           forwarding it. Computed independently (see unbuyable(), above)
           for the same reason paint() no longer reads wc-atc-state.js's
           class: this is the other half of the 22 Sep 2026 flicker fix —
           without it, a shopper tapping a SOLD OUT button more than once
           (an understandable reaction to a button that looks unresponsive)
           fired a fresh ADDING... flash on every tap, each one racing the
           correct SOLD OUT repaint that followed it. */
        if (unbuyable()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        // Otherwise this tap is a real add-to-cart, being forwarded to the
        // hidden native button. Show the busy state the instant the tap
        // happens rather than waiting on the native button's own
        // class/attribute to flip, so there is never a gap with no feedback
        // at all. watchRealButton() below still confirms and clears it from
        // the real button's actual state; armBusyTimeout is only a safety
        // net in case that confirmation is ever missed.
        setBusy(true);
        armBusyTimeout();
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      if (atc.blur) atc.blur();   // clears any lingering focus/tap styling
      openStep(lastNext);
    }, true);
  }

  /* ---------- real submit button's ~2s loading state ---------- */

  var busyTimeout = null;

  function setBusy(v) {
    if (v === busy) return;
    busy = v;
    paint();
  }

  function armBusyTimeout() {
    clearTimeout(busyTimeout);
    // Safety net only: if, for any reason, the real button's own loading
    // signal is never observed (a theme update changes how it marks
    // itself busy, a slow network, etc.), this guarantees the button does
    // not stay stuck in the busy/pulsing state forever.
    busyTimeout = setTimeout(function () { setBusy(false); }, 6000);
  }

  function watchRealButton() {
    var real = document.querySelector('[id^="ProductSubmitButton"]');
    if (!real || !window.MutationObserver) return;

    function readBusy() {
      return real.classList.contains('loading') ||
        real.getAttribute('aria-disabled') === 'true' ||
        real.disabled === true;
    }

    new MutationObserver(function () {
      var next = readBusy();
      clearTimeout(busyTimeout);
      setBusy(next);
    }).observe(real, { attributes: true, attributeFilter: ['class', 'aria-disabled', 'disabled'] });
  }

  function watch() {
    var host = document.querySelector('product-info') || document.querySelector('.product__info-wrapper');
    if (!host || !window.MutationObserver) return;
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = null; paint(); }, 100);
    }).observe(host, { childList: true, subtree: true });
  }

  function boot() {
    if (!document.querySelector('.dummy-add-to-cart')) return;   // not a product page
    if (!document.querySelector('variant-selects')) {            // single-variant product — nothing to guide
      /* Nothing to choose, so it is ready to add from the start: show the
         completed brand-orange button straight away (unless the theme has
         rendered it as SOLD OUT, which it marks disabled). */
      var simple = document.querySelector('.dummy-add-to-cart');
      if (simple && !simple.disabled) {
        simple.classList.add('wc-guided-complete');
        simple.style.setProperty('background-color', ORANGE, 'important');
        simple.style.setProperty('border-color', ORANGE, 'important');
        simple.style.setProperty('color', '#fff', 'important');
        simple.style.removeProperty('opacity');
      }
      return;
    }

    style();
    watchPress();
    watchTouch();
    watchClicks();
    watch();
    watchRealButton();

    document.addEventListener('wc-buckle:change', paint);
    document.addEventListener('change', function () { setTimeout(paint, 80); }, true);

    /* Self-correcting safety net: catches any case where a drawer's own
       confirm action does not produce a mutation or event this file is
       already listening for. */
    setInterval(paint, 700);

    setTimeout(paint, 150);
    paint();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
